import { useState } from 'react';
import { 
    Monitor, 
    Plus, 
    Play, 
    Square, 
    Trash2, 
    Settings, 
    ExternalLink,
    HardDrive,
    Cpu,
    MemoryStick,
    DollarSign,
    AlertCircle,
    CheckCircle,
    Loader,
    Terminal,
    X
} from 'lucide-react';
import { mockWorkstations, Workstation } from '../../../mock/data';

const GraphicalInterface: React.FC = () => {
    const [workstations, setWorkstations] = useState<Workstation[]>(mockWorkstations);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [selectedWorkstation, setSelectedWorkstation] = useState<Workstation | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    // 创建工作站表单状态
    const [createForm, setCreateForm] = useState({
        name: '',
        type: 'windows' as 'windows' | 'linux',
        cpuCores: 4,
        memory: 16,
        storage: 100,
        template: ''
    });

    // 获取状态图标和颜色
    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'running':
                return <CheckCircle className="w-5 h-5 text-green-500" />;
            case 'stopped':
                return <Square className="w-5 h-5 text-gray-500" />;
            case 'starting':
                return <Loader className="w-5 h-5 text-blue-500 animate-spin" />;
            case 'stopping':
                return <Loader className="w-5 h-5 text-orange-500 animate-spin" />;
            default:
                return <AlertCircle className="w-5 h-5 text-red-500" />;
        }
    };

    const getStatusText = (status: string) => {
        switch (status) {
            case 'running': return '运行中';
            case 'stopped': return '已停止';
            case 'starting': return '启动中';
            case 'stopping': return '停止中';
            default: return '未知';
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'running': return 'bg-green-100 text-green-800';
            case 'stopped': return 'bg-gray-100 text-gray-800';
            case 'starting': return 'bg-blue-100 text-blue-800';
            case 'stopping': return 'bg-orange-100 text-orange-800';
            default: return 'bg-red-100 text-red-800';
        }
    };

    // 启动工作站
    const handleStartWorkstation = async (id: string) => {
        setIsLoading(true);
        try {
            setWorkstations(prev => prev.map(ws => 
                ws.id === id ? { ...ws, status: 'starting' } : ws
            ));

            // 模拟启动过程
            await new Promise(resolve => setTimeout(resolve, 3000));

            setWorkstations(prev => prev.map(ws => 
                ws.id === id ? { ...ws, status: 'running', lastAccessAt: new Date().toISOString() } : ws
            ));

            alert('工作站启动成功！');
        } catch (error) {
            alert('启动失败，请稍后重试');
        } finally {
            setIsLoading(false);
        }
    };

    // 停止工作站
    const handleStopWorkstation = async (id: string) => {
        setIsLoading(true);
        try {
            setWorkstations(prev => prev.map(ws => 
                ws.id === id ? { ...ws, status: 'stopping' } : ws
            ));

            // 模拟停止过程
            await new Promise(resolve => setTimeout(resolve, 2000));

            setWorkstations(prev => prev.map(ws => 
                ws.id === id ? { ...ws, status: 'stopped' } : ws
            ));

            alert('工作站已停止');
        } catch (error) {
            alert('停止失败，请稍后重试');
        } finally {
            setIsLoading(false);
        }
    };

    // 删除工作站
    const handleDeleteWorkstation = async (id: string) => {
        if (!confirm('确定要删除这个工作站吗？此操作不可恢复。')) {
            return;
        }

        setIsLoading(true);
        try {
            await new Promise(resolve => setTimeout(resolve, 1000));
            setWorkstations(prev => prev.filter(ws => ws.id !== id));
            alert('工作站已删除');
        } catch (error) {
            alert('删除失败，请稍后重试');
        } finally {
            setIsLoading(false);
        }
    };

    // 连接工作站
    const handleConnectWorkstation = (workstation: Workstation) => {
        if (workstation.status !== 'running') {
            alert('请先启动工作站');
            return;
        }

        // 模拟连接
        const connectionType = workstation.type === 'windows' ? 'RDP' : 'VNC';
        alert(`正在通过 ${connectionType} 连接到 ${workstation.name}...`);
        
        // 更新最后访问时间
        setWorkstations(prev => prev.map(ws => 
            ws.id === workstation.id ? { ...ws, lastAccessAt: new Date().toISOString() } : ws
        ));
    };

    // 创建工作站
    const handleCreateWorkstation = async () => {
        if (!createForm.name.trim()) {
            alert('请输入工作站名称');
            return;
        }

        setIsLoading(true);
        try {
            const newWorkstation: Workstation = {
                id: `ws_${Date.now()}`,
                name: createForm.name,
                type: createForm.type,
                status: 'stopped',
                cpuCores: createForm.cpuCores,
                memory: createForm.memory,
                storage: createForm.storage,
                cost: calculateCost(createForm.cpuCores, createForm.memory, createForm.storage),
                createdAt: new Date().toISOString(),
                template: createForm.template || undefined
            };

            await new Promise(resolve => setTimeout(resolve, 2000));
            setWorkstations(prev => [...prev, newWorkstation]);
            setShowCreateModal(false);
            setCreateForm({
                name: '',
                type: 'windows',
                cpuCores: 4,
                memory: 16,
                storage: 100,
                template: ''
            });
            alert('工作站创建成功！');
        } catch (error) {
            alert('创建失败，请稍后重试');
        } finally {
            setIsLoading(false);
        }
    };

    // 计算费用
    const calculateCost = (cpuCores: number, memory: number, storage: number) => {
        return parseFloat((cpuCores * 0.5 + memory * 0.1 + storage * 0.01).toFixed(2));
    };

    // 格式化时间
    const formatTime = (dateString: string) => {
        return new Date(dateString).toLocaleString('zh-CN');
    };

    // 格式化运行时间
    const formatUptime = (createdAt: string, status: string) => {
        if (status !== 'running') return '-';
        const now = new Date();
        const created = new Date(createdAt);
        const diff = now.getTime() - created.getTime();
        const hours = Math.floor(diff / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        return `${hours}小时${minutes}分钟`;
    };

    // 预设模板
    const templates = {
        windows: [
            'Windows Server 2019 基础版',
            'Windows Server 2019 + Visual Studio',
            'Windows Server 2019 + MATLAB',
            'Windows Server 2019 + Office套件'
        ],
        linux: [
            'CentOS 7 基础版',
            'CentOS 7 + Development Tools',
            'Ubuntu 20.04 + CUDA 11.8',
            'Ubuntu 20.04 + Docker'
        ]
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-6">
            <div className="max-w-7xl mx-auto space-y-8">
                {/* 页面标题 */}
                <div className="relative overflow-hidden bg-white/80 backdrop-blur-sm rounded-2xl border border-white/20 shadow-xl p-8">
                    <div className="absolute inset-0 bg-gradient-to-r from-blue-600/5 to-indigo-600/5"></div>
                    <div className="relative flex items-center justify-between">
                        <div className="flex items-center space-x-5">
                            <div className="relative flex-shrink-0">
                                <div className="w-14 h-14 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 rounded-xl flex items-center justify-center shadow-xl transform hover:scale-105 transition-transform">
                                    <Monitor className="w-7 h-7 text-white" />
                                </div>
                                <div className="absolute -top-1 -right-1 w-5 h-5 bg-gradient-to-r from-cyan-400 to-blue-500 rounded-full flex items-center justify-center">
                                    <Terminal className="w-2.5 h-2.5 text-white" />
                                </div>
                            </div>
                            <div className="flex flex-col justify-center">
                                <h1 className="text-3xl font-bold bg-gradient-to-r from-indigo-600 to-pink-600 bg-clip-text text-transparent leading-tight">
                                    图形界面
                                </h1>
                                <p className="text-gray-600 mt-2 text-base leading-relaxed">管理您的Windows和Linux工作站</p>
                            </div>
                        </div>
                        <button
                            onClick={() => setShowCreateModal(true)}
                            className="group flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
                        >
                            <Plus className="w-5 h-5 group-hover:rotate-90 transition-transform duration-300" />
                            <span>创建工作站</span>
                        </button>
                    </div>
                </div>

                {/* 统计卡片 */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                    <div className="group bg-white/80 backdrop-blur-sm rounded-2xl border border-white/20 shadow-lg hover:shadow-xl transition-all duration-300 p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-600 mb-1">总工作站</p>
                                <p className="text-3xl font-bold text-gray-900">{workstations.length}</p>
                            </div>
                            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                                <Monitor className="w-6 h-6 text-white" />
                            </div>
                        </div>
                    </div>

                    <div className="group bg-white/80 backdrop-blur-sm rounded-2xl border border-white/20 shadow-lg hover:shadow-xl transition-all duration-300 p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-600 mb-1">运行中</p>
                                <p className="text-3xl font-bold text-green-600">
                                    {workstations.filter(ws => ws.status === 'running').length}
                                </p>
                            </div>
                            <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                                <CheckCircle className="w-6 h-6 text-white" />
                            </div>
                        </div>
                    </div>

                    <div className="group bg-white/80 backdrop-blur-sm rounded-2xl border border-white/20 shadow-lg hover:shadow-xl transition-all duration-300 p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-600 mb-1">Windows</p>
                                <p className="text-3xl font-bold text-blue-600">
                                    {workstations.filter(ws => ws.type === 'windows').length}
                                </p>
                            </div>
                            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                                <Terminal className="w-6 h-6 text-white" />
                            </div>
                        </div>
                    </div>

                    <div className="group bg-white/80 backdrop-blur-sm rounded-2xl border border-white/20 shadow-lg hover:shadow-xl transition-all duration-300 p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-600 mb-1">Linux</p>
                                <p className="text-3xl font-bold text-purple-600">
                                    {workstations.filter(ws => ws.type === 'linux').length}
                                </p>
                            </div>
                            <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                                <Terminal className="w-6 h-6 text-white" />
                            </div>
                        </div>
                    </div>
                </div>

                {/* 工作站列表 */}
                <div className="bg-white/80 backdrop-blur-sm rounded-2xl border border-white/20 shadow-xl">
                    <div className="p-6 border-b border-gray-200/50">
                        <h2 className="text-xl font-bold text-gray-900 flex items-center">
                            <div className="w-2 h-6 bg-gradient-to-b from-blue-500 to-indigo-600 rounded-full mr-3"></div>
                            工作站列表
                        </h2>
                    </div>

                    {workstations.length === 0 ? (
                        <div className="text-center py-16">
                            <div className="w-20 h-20 bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl flex items-center justify-center mx-auto mb-6">
                                <Monitor className="w-10 h-10 text-gray-400" />
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 mb-2">暂无工作站</h3>
                            <p className="text-gray-500 mb-6">创建您的第一个工作站开始使用</p>
                            <button
                                onClick={() => setShowCreateModal(true)}
                                className="inline-flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
                            >
                                <Plus className="w-5 h-5" />
                                <span>创建工作站</span>
                            </button>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-gray-50/50">
                                    <tr>
                                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">工作站信息</th>
                                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">配置</th>
                                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">状态</th>
                                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">运行时间</th>
                                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">费用/小时</th>
                                        <th className="px-6 py-4 text-center text-sm font-semibold text-gray-900">操作</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200/50">
                                    {workstations.map((workstation) => (
                                        <tr key={workstation.id} className="hover:bg-blue-50/50 transition-colors duration-200">
                                            <td className="px-6 py-4">
                                                <div className="flex items-center space-x-4">
                                                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center">
                                                        <Terminal className="w-6 h-6 text-white" />
                                                    </div>
                                                    <div>
                                                        <div className="font-semibold text-gray-900">{workstation.name}</div>
                                                        <div className="text-sm text-gray-500">
                                                            {workstation.type === 'windows' ? 'Windows工作站' : 'Linux工作站'}
                                                        </div>
                                                        {workstation.template && (
                                                            <div className="text-xs text-blue-600 mt-1 bg-blue-50 px-2 py-1 rounded">
                                                                {workstation.template}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex items-center space-x-4 text-sm">
                                                    <div className="flex items-center space-x-1">
                                                        <Cpu className="w-4 h-4 text-gray-400" />
                                                        <span>{workstation.cpuCores}核</span>
                                                    </div>
                                                    <div className="flex items-center space-x-1">
                                                        <MemoryStick className="w-4 h-4 text-gray-400" />
                                                        <span>{workstation.memory}GB</span>
                                                    </div>
                                                    <div className="flex items-center space-x-1">
                                                        <HardDrive className="w-4 h-4 text-gray-400" />
                                                        <span>{workstation.storage}GB</span>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex items-center space-x-2">
                                                    {getStatusIcon(workstation.status)}
                                                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(workstation.status)}`}>
                                                        {getStatusText(workstation.status)}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-600">
                                                {formatUptime(workstation.createdAt, workstation.status)}
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex items-center space-x-1 text-sm font-semibold text-green-600">
                                                    <DollarSign className="w-4 h-4" />
                                                    <span>¥{workstation.cost}</span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex items-center justify-center space-x-2">
                                                    {workstation.status === 'stopped' ? (
                                                        <button
                                                            onClick={() => handleStartWorkstation(workstation.id)}
                                                            disabled={isLoading}
                                                            className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors duration-200 disabled:opacity-50"
                                                            title="启动"
                                                        >
                                                            <Play className="w-4 h-4" />
                                                        </button>
                                                    ) : workstation.status === 'running' ? (
                                                        <>
                                                            <button
                                                                onClick={() => handleConnectWorkstation(workstation)}
                                                                className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors duration-200"
                                                                title="连接"
                                                            >
                                                                <ExternalLink className="w-4 h-4" />
                                                            </button>
                                                            <button
                                                                onClick={() => handleStopWorkstation(workstation.id)}
                                                                disabled={isLoading}
                                                                className="p-2 text-orange-600 hover:bg-orange-50 rounded-lg transition-colors duration-200 disabled:opacity-50"
                                                                title="停止"
                                                            >
                                                                <Square className="w-4 h-4" />
                                                            </button>
                                                        </>
                                                    ) : (
                                                        <div className="p-2">
                                                            <Loader className="w-4 h-4 text-gray-400 animate-spin" />
                                                        </div>
                                                    )}
                                                    <button
                                                        onClick={() => setSelectedWorkstation(workstation)}
                                                        className="p-2 text-gray-600 hover:bg-gray-50 rounded-lg transition-colors duration-200"
                                                        title="设置"
                                                    >
                                                        <Settings className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeleteWorkstation(workstation.id)}
                                                        disabled={isLoading || workstation.status === 'running'}
                                                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-200 disabled:opacity-50"
                                                        title="删除"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {/* 创建工作站模态框 */}
            {showCreateModal && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
                    <div className="bg-white/95 backdrop-blur-sm rounded-2xl border border-white/20 shadow-2xl p-8 w-full max-w-2xl mx-4">
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="text-2xl font-bold text-gray-900">创建工作站</h2>
                            <button
                                onClick={() => setShowCreateModal(false)}
                                className="p-2 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                            >
                                <X className="w-5 h-5 text-gray-500" />
                            </button>
                        </div>
                        
                        <div className="space-y-6">
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                    工作站名称
                                </label>
                                <input
                                    type="text"
                                    value={createForm.name}
                                    onChange={(e) => setCreateForm(prev => ({ ...prev, name: e.target.value }))}
                                    className="w-full px-4 py-3 bg-white/80 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                                    placeholder="输入工作站名称"
                                />
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                        操作系统类型
                                    </label>
                                    <select
                                        value={createForm.type}
                                        onChange={(e) => setCreateForm(prev => ({ ...prev, type: e.target.value as 'windows' | 'linux' }))}
                                        className="w-full px-4 py-3 bg-white/80 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                                    >
                                        <option value="windows">Windows</option>
                                        <option value="linux">Linux</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                        模板选择（可选）
                                    </label>
                                    <select
                                        value={createForm.template}
                                        onChange={(e) => setCreateForm(prev => ({ ...prev, template: e.target.value }))}
                                        className="w-full px-4 py-3 bg-white/80 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                                    >
                                        <option value="">选择模板</option>
                                        {templates[createForm.type].map((template, index) => (
                                            <option key={index} value={template}>{template}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="grid grid-cols-3 gap-4">
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                        CPU核数
                                    </label>
                                    <select
                                        value={createForm.cpuCores}
                                        onChange={(e) => setCreateForm(prev => ({ ...prev, cpuCores: parseInt(e.target.value) }))}
                                        className="w-full px-4 py-3 bg-white/80 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                                    >
                                        <option value={2}>2核</option>
                                        <option value={4}>4核</option>
                                        <option value={8}>8核</option>
                                        <option value={16}>16核</option>
                                        <option value={32}>32核</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                        内存(GB)
                                    </label>
                                    <select
                                        value={createForm.memory}
                                        onChange={(e) => setCreateForm(prev => ({ ...prev, memory: parseInt(e.target.value) }))}
                                        className="w-full px-4 py-3 bg-white/80 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                                    >
                                        <option value={8}>8GB</option>
                                        <option value={16}>16GB</option>
                                        <option value={32}>32GB</option>
                                        <option value={64}>64GB</option>
                                        <option value={128}>128GB</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                        存储(GB)
                                    </label>
                                    <select
                                        value={createForm.storage}
                                        onChange={(e) => setCreateForm(prev => ({ ...prev, storage: parseInt(e.target.value) }))}
                                        className="w-full px-4 py-3 bg-white/80 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                                    >
                                        <option value={100}>100GB</option>
                                        <option value={200}>200GB</option>
                                        <option value={500}>500GB</option>
                                        <option value={1000}>1TB</option>
                                        <option value={2000}>2TB</option>
                                    </select>
                                </div>
                            </div>

                            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-200">
                                <div className="flex items-center space-x-2">
                                    <DollarSign className="w-5 h-5 text-blue-600" />
                                    <span className="font-semibold text-blue-800">
                                        预估费用: ¥{calculateCost(createForm.cpuCores, createForm.memory, createForm.storage)}/小时
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div className="flex justify-end space-x-4 mt-8">
                            <button
                                onClick={() => setShowCreateModal(false)}
                                className="px-6 py-3 border border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 transition-colors duration-200"
                            >
                                取消
                            </button>
                            <button
                                onClick={handleCreateWorkstation}
                                disabled={isLoading}
                                className="px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 disabled:opacity-50 shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
                            >
                                {isLoading ? '创建中...' : '创建'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* 工作站详情模态框 */}
            {selectedWorkstation && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
                    <div className="bg-white/95 backdrop-blur-sm rounded-2xl border border-white/20 shadow-2xl p-8 w-full max-w-3xl mx-4">
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="text-2xl font-bold text-gray-900">工作站详情</h2>
                            <button
                                onClick={() => setSelectedWorkstation(null)}
                                className="p-2 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                            >
                                <X className="w-5 h-5 text-gray-500" />
                            </button>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            <div className="space-y-6">
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900 mb-4">基本信息</h3>
                                    <div className="space-y-3">
                                        <div className="flex justify-between items-center py-2 border-b border-gray-100">
                                            <span className="text-sm text-gray-600">名称:</span>
                                            <span className="font-medium text-gray-900">{selectedWorkstation.name}</span>
                                        </div>
                                        <div className="flex justify-between items-center py-2 border-b border-gray-100">
                                            <span className="text-sm text-gray-600">类型:</span>
                                            <span className="font-medium text-gray-900">
                                                {selectedWorkstation.type === 'windows' ? 'Windows' : 'Linux'}
                                            </span>
                                        </div>
                                        <div className="flex justify-between items-center py-2 border-b border-gray-100">
                                            <span className="text-sm text-gray-600">状态:</span>
                                            <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(selectedWorkstation.status)}`}>
                                                {getStatusText(selectedWorkstation.status)}
                                            </span>
                                        </div>
                                        <div className="flex justify-between items-center py-2 border-b border-gray-100">
                                            <span className="text-sm text-gray-600">创建时间:</span>
                                            <span className="font-medium text-gray-900 text-sm">
                                                {formatTime(selectedWorkstation.createdAt)}
                                            </span>
                                        </div>
                                        {selectedWorkstation.lastAccessAt && (
                                            <div className="flex justify-between items-center py-2 border-b border-gray-100">
                                                <span className="text-sm text-gray-600">最后访问:</span>
                                                <span className="font-medium text-gray-900 text-sm">
                                                    {formatTime(selectedWorkstation.lastAccessAt)}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-6">
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900 mb-4">配置信息</h3>
                                    <div className="space-y-3">
                                        <div className="flex justify-between items-center py-2 border-b border-gray-100">
                                            <span className="text-sm text-gray-600">CPU:</span>
                                            <span className="font-medium text-gray-900">{selectedWorkstation.cpuCores}核</span>
                                        </div>
                                        <div className="flex justify-between items-center py-2 border-b border-gray-100">
                                            <span className="text-sm text-gray-600">内存:</span>
                                            <span className="font-medium text-gray-900">{selectedWorkstation.memory}GB</span>
                                        </div>
                                        <div className="flex justify-between items-center py-2 border-b border-gray-100">
                                            <span className="text-sm text-gray-600">存储:</span>
                                            <span className="font-medium text-gray-900">{selectedWorkstation.storage}GB</span>
                                        </div>
                                        <div className="flex justify-between items-center py-2 border-b border-gray-100">
                                            <span className="text-sm text-gray-600">费用:</span>
                                            <span className="font-medium text-green-600">¥{selectedWorkstation.cost}/小时</span>
                                        </div>
                                        {selectedWorkstation.template && (
                                            <div className="flex justify-between items-center py-2 border-b border-gray-100">
                                                <span className="text-sm text-gray-600">模板:</span>
                                                <span className="font-medium text-gray-900 text-sm">{selectedWorkstation.template}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="mt-8 p-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200">
                            <h3 className="text-lg font-semibold text-blue-900 mb-3">连接信息</h3>
                            <div className="text-sm text-blue-800 space-y-2">
                                {selectedWorkstation.type === 'windows' ? (
                                    <p>使用RDP客户端连接，端口: 3389</p>
                                ) : (
                                    <p>使用VNC客户端连接，端口: 5901</p>
                                )}
                                <p>连接地址将在工作站启动后显示</p>
                            </div>
                        </div>

                        <div className="flex justify-end space-x-4 mt-8">
                            <button
                                onClick={() => setSelectedWorkstation(null)}
                                className="px-6 py-3 border border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 transition-colors duration-200"
                            >
                                关闭
                            </button>
                            {selectedWorkstation.status === 'running' && (
                                <button
                                    onClick={() => handleConnectWorkstation(selectedWorkstation)}
                                    className="px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 flex items-center space-x-2"
                                >
                                    <ExternalLink className="w-4 h-4" />
                                    <span>连接工作站</span>
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default GraphicalInterface;
